import { ExternalLink, FileText, PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { safeUrl } from "@/lib/sanitize";
import { listMediaBySlug } from "@/features/knowledge/api";
import type { KbMediaAsset } from "@/features/knowledge/types";

interface HelpVideosPopupProps {
  /** Media slug for this screen's help-video/tool node. */
  screenSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function iconFor(assetType: KbMediaAsset["assetType"]) {
  if (assetType === "VIDEO") return PlayCircle;
  if (assetType === "PDF") return FileText;
  return ExternalLink;
}

/**
 * Per-screen list of help videos / PDFs / tool links (legacy `Help::videos`).
 * Firm administrators also get a "Return to onboarding wizard" link that re-opens
 * the full-page `/onboarding` flow.
 */
export function HelpVideosPopup({ screenSlug, open, onOpenChange }: HelpVideosPopupProps) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<KbMediaAsset[]>([]);
  const isFirmAdmin = useAuthStore(
    (s) => s.user?.account_type === "firm" && s.user.role === "country_director",
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    listMediaBySlug(screenSlug)
      .then((data) => !cancelled && setItems(data))
      .catch(() => !cancelled && toast.error("Could not load help videos."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, screenSlug]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Help videos &amp; tools</DialogTitle>
          <DialogDescription>Resources for this screen.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : items.length ? (
          <ul className="divide-y">
            {items.map((item) => {
              const Icon = iconFor(item.assetType);
              return (
                <li key={item.id}>
                  <a
                    href={safeUrl(item.href)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:bg-accent flex items-center gap-3 rounded-md px-2 py-2 text-sm"
                  >
                    <Icon className="text-muted-foreground size-4 shrink-0" />
                    <span>{item.description}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">No help videos for this screen yet.</p>
        )}

        {isFirmAdmin && (
          <Link
            to="/onboarding"
            onClick={() => onOpenChange(false)}
            className="text-primary text-left text-xs hover:underline"
          >
            ↻ Return to onboarding wizard
          </Link>
        )}
      </DialogContent>
    </Dialog>
  );
}
